// OP-3605 — A transferência (ver ESL1; operar ESL5; aprovar ESL6): uma ação
// por status, e a conferência peça a peça no fim. Receber é "a caixa chegou";
// conferir é "o que havia dentro" — e é a conferência que devolve o material
// ao estoque do destino.
import { FC, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useEstoqueTransferencia } from '../../../../hooks/estoque'
import { usePerfilEstoque } from '../../../../hooks/estoque/perfil'
import {
  aprovarEstoqueTransferencia, cancelarEstoqueTransferencia, conferirEstoqueTransferencia,
  despacharEstoqueTransferencia, iniciarSeparacaoEstoqueTransferencia, receberEstoqueTransferencia,
} from '../../../../Service/estoque'
import { DecisaoConferencia, TransferenciaItem } from '../../../../types/estoque'
import {
  BD, SEVERIDADE, Button, Card, DetailLayout, Field, Input, ListaCartoes, PageSubtitle, PageTitle, Select,
  Spinner, StatusBadge, Table, TabelaWrap,
} from '../../styles'
import { fmtDate, fmtDateTime } from '../../statusUi'
import { rotuloDecisao } from '../conferencia'
import { Stepper } from '../Stepper'
import { Decisao, AcaoDecisao } from '../Decisao'
import { tratarErro } from '../erros'
import { RetornoLeitura, acharPorCodigo } from '../leitura'
import { LeitorContinuo } from '../LeitorContinuo'
import {
  CAPACIDADE_DA_ACAO, LinhaConferenciaTransferencia, STATUS_TRANSFERENCIA_UI, acaoDoStatusTransferencia, decisoesDaLinha, faltaDaConferencia, faltasDeSaldo, impedimentoAprovarPorAutoria, impedimentoConferenciaTransferencia, impedimentoConfirmarCancelamento, impedimentoDoLado, cancelamentoDaTransferencia, desdeDaTransferencia, montarConferenciaTransferencia, podeCancelarTransferencia, chegadaDaLinha, textoDoEvento, textoFaltaDeSaldo,
} from './regras'
import { LOCAL_EXECUTION } from '../../../../utils/featureFlag'

const Bloco: FC<{ titulo: string; children: any }> = ({ titulo, children }) => (
  <Card style={{ padding: 16, marginBottom: 16 }}><div style={{ fontWeight: 600, marginBottom: 8 }}>{titulo}</div>{children}</Card>
)

const ROTULO_ACAO: Record<string, string> = {
  APROVAR: 'Aprovar transferência', SEPARAR: 'Iniciar separação', DESPACHAR: 'Despachar',
  RECEBER: 'Confirmar recebimento', CONFERIR: 'Conferir',
}

export const EstoqueTransferencia: FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const idNum = Number(id) || null
  const { pode, usuarioId } = usePerfilEstoque()
  const { data: t, isLoading, isError, isUnauthorized, mutate } = useEstoqueTransferencia(idNum)
  const [busy, setBusy] = useState(false)
  const [transportadora, setTransportadora] = useState('')
  const [codigoRastreio, setCodigoRastreio] = useState('')
  const [conf, setConf] = useState<Record<number, LinhaConferenciaTransferencia>>({})
  const [confirmandoCancelar, setConfirmandoCancelar] = useState(false)
  const [motivoCancelamento, setMotivoCancelamento] = useState('')

  if (isUnauthorized && !LOCAL_EXECUTION) return <Navigate to="/auth" replace />
  if (isLoading) return <Card><div style={{ textAlign: 'center', padding: 24 }}><Spinner /></div></Card>
  if (isError || !t) {
    return (
      <>
        <PageTitle>Transferência</PageTitle>
        <Card><div style={{ padding: 24, color: BD.grayText }}>
          Transferência não encontrada ou fora do seu escopo. <Button $variant="secondary" onClick={() => navigate('/interno/estoque/transferencias')}>Voltar</Button>
        </div></Card>
      </>
    )
  }

  const executar = async (acao: () => Promise<any>, ok: string, limpar?: () => void) => {
    setBusy(true)
    try {
      await acao()
      toast.success(ok)
      if (limpar) limpar()
      await mutate()
    } catch (err) {
      const e = tratarErro(err)
      if (e.tipo === 'sessao' && !LOCAL_EXECUTION) { navigate('/auth'); return }
      toast.error(e.texto)
      if (e.tipo === 'estado') await mutate()
    } finally {
      setBusy(false)
    }
  }

  const ui = STATUS_TRANSFERENCIA_UI[t.status] ?? { bg: '#F3F4F6', color: '#374151', label: t.status }
  const acao = acaoDoStatusTransferencia(t.status)
  const temPermissao = acao ? pode(CAPACIDADE_DA_ACAO[acao]) : false
  const doLado = impedimentoDoLado(acao, t)
  const podeAgir = temPermissao && !doLado
  const impConf = impedimentoConferenciaTransferencia(t.itens, conf)
  const cancelavel = podeCancelarTransferencia(t.status)
  const setLinha = (l: TransferenciaItem, patch: Partial<LinhaConferenciaTransferencia>) =>
    setConf((c) => ({ ...c, [l.id]: { ...(c[l.id] || { decisao: '', quantidadeRecebida: '', observacao: '' }), ...patch } }))

  const lerChegada = (codigo: string): RetornoLeitura => {
    const l = acharPorCodigo(t.itens.filter((x) => x.controle === 'SERIAL'), codigo, (x) => [x.serial, x.patrimonio])
    if (!l) return { texto: `${codigo} não veio nesta transferência.`, tom: 'erro' }
    const atual = conf[l.id]?.decisao
    if (atual) return { texto: `${l.serial ?? codigo} já conferida: ${rotuloDecisao('CHEGADA', atual)}.`, tom: 'aviso' }
    setLinha(l, { decisao: 'OK' })
    return { texto: `✓ ${l.serial ?? codigo} · ${rotuloDecisao('CHEGADA', 'OK')}. Se estiver avariada, troque na lista.`, tom: 'ok' }
  }

  const faltas = faltasDeSaldo(t.itens)
  // Aprovar e despachar rodam a MESMA guarda no service (exigirLivreConsumiveis):
  // avisar só numa delas repetiria o N1 um estado adiante.
  const impSaldo = acao === 'APROVAR' || acao === 'DESPACHAR' ? textoFaltaDeSaldo(faltas, t.origemNome) : null

  const disparar = () => {
    if (!acao) return
    if (acao === 'APROVAR') return executar(() => aprovarEstoqueTransferencia(t.id), 'Transferência aprovada')
    if (acao === 'SEPARAR') return executar(() => iniciarSeparacaoEstoqueTransferencia({ id: t.id }), 'Separação iniciada')
    if (acao === 'DESPACHAR') {
      return executar(() => despacharEstoqueTransferencia({ id: t.id, transportadora: transportadora.trim() || null, codigoRastreio: codigoRastreio.trim() || null }),
        'Transferência despachada', () => { setTransportadora(''); setCodigoRastreio('') })
    }
    if (acao === 'RECEBER') return executar(() => receberEstoqueTransferencia({ id: t.id }), 'Recebimento registrado')
    return undefined
  }

  const conferir = () => executar(
    () => conferirEstoqueTransferencia({ id: t.id, itens: montarConferenciaTransferencia(t.itens, conf) }),
    'Conferência registrada',
    () => setConf({}),
  )

  const impedimentoDaAcao = (): string | null => {
    if (!acao) return null
    if (!temPermissao) {
      return acao === 'APROVAR'
        ? 'Aprovar transferência é alçada da coordenação (permissão "Aprovar transferência").'
        : 'Esta ação exige a permissão "Transferir entre praças".'
    }
    if (doLado) return doLado
    if (acao === 'APROVAR') {
      const autoria = impedimentoAprovarPorAutoria(t.criadoPorId, usuarioId)
      if (autoria) return autoria
    }
    if (acao === 'APROVAR' || acao === 'DESPACHAR') {
      // A frase inteira mora no impedimento, ao lado do botão: é onde ela é
      // lida nas duas larguras. Repeti-la no aviso do trilho só ocupava espaço.
      return impSaldo ? `${impSaldo} Enquanto isso, este botão fica desligado — não é falha da tela.` : null
    }
    if (acao === 'CONFERIR') return impConf
    return null
  }

  const principal: AcaoDecisao | null = acao
    ? {
      rotulo: ROTULO_ACAO[acao],
      rodar: acao === 'CONFERIR' ? conferir : disparar,
      impedimento: impedimentoDaAcao(),
    }
    : null

  // Cancelar é destrutivo: dois passos, e com saída. O segundo botão nunca
  // ocupa o lugar do primeiro — quem tocou sem querer tem "Voltar" ao lado.
  const desarmarCancelamento = () => { setConfirmandoCancelar(false); setMotivoCancelamento('') }
  const secundarias: AcaoDecisao[] = cancelavel && pode('TRANSFERIR')
    ? (confirmandoCancelar
      ? [
        // "Voltar" ocupa o slot que a pessoa acabou de tocar: um segundo toque
        // no mesmo lugar desfaz, nunca confirma o cancelamento.
        { rotulo: 'Voltar', rodar: desarmarCancelamento, variante: 'secondary' as const },
        {
          rotulo: `Confirmar o cancelamento de ${t.numero}`,
          rodar: () => executar(() => cancelarEstoqueTransferencia(t.id, motivoCancelamento.trim()), 'Transferência cancelada', desarmarCancelamento),
          impedimento: impedimentoConfirmarCancelamento(motivoCancelamento),
          variante: 'secondary' as const,
        },
      ]
      : [{ rotulo: 'Cancelar transferência', rodar: () => setConfirmandoCancelar(true), variante: 'secondary' as const }])
    : []
  const campoMotivoCancelamento = confirmandoCancelar ? (
    <Field style={{ marginBottom: 8 }}>
      <label>Motivo do cancelamento</label>
      <Input aria-label="Motivo do cancelamento" value={motivoCancelamento} maxLength={400} onChange={(e) => setMotivoCancelamento(e.target.value)} />
    </Field>
  ) : null
  const historico = t.historico ?? []
  const cancelamento = cancelamentoDaTransferencia(historico)
  const desde = desdeDaTransferencia(t)

  return (
    <DetailLayout $contido>
      <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <PageTitle>{t.numero} <StatusBadge $bg={ui.bg} $color={ui.color}>{ui.label}</StatusBadge></PageTitle>
          <PageSubtitle>
            <b>{t.origemNome}</b> → <b>{t.destinoNome}</b>
            {t.transportadora ? ` · ${t.transportadora}` : ''}{t.codigoRastreio ? ` · rastreio ${t.codigoRastreio}` : ''}
            {' · '}previsão {fmtDate(t.previsaoChegada)} · aberta {fmtDateTime(t.criadoEm)}
            {t.criadoPorNome && <> por {t.criadoPorNome}</>}
            {` · ${t.ladoSolicitante === 'DESTINO' ? 'pedida pelo destino' : 'oferecida pela origem'}`}
            {t.requisicaoNumero && (t.noDestino
              ? <> · para o pedido <a href={`/interno/estoque/fila/${t.requisicaoId}`} onClick={(e) => { e.preventDefault(); navigate(`/interno/estoque/fila/${t.requisicaoId}`) }}>{t.requisicaoNumero}</a></>
              : <> · para o pedido {t.requisicaoNumero}</>)}
            {t.aprovadoEm && <> · aprovada {fmtDateTime(t.aprovadoEm)}</>}
            {t.aprovadoEm && t.aprovadoPorNome && <> por {t.aprovadoPorNome}</>}
            {t.despachadoEm && <> · despachada {fmtDateTime(t.despachadoEm)}</>}
            {t.recebidoEm && <> · recebida {fmtDateTime(t.recebidoEm)}</>}
            {t.conferidoEm && <> · conferida {fmtDateTime(t.conferidoEm)}</>}
            {cancelamento && <> · cancelada {fmtDateTime(cancelamento.criadoEm)}</>}
            {cancelamento?.usuarioNome && <> por {cancelamento.usuarioNome}</>}
            {t.motivo && <div style={{ marginTop: 4 }}>{t.motivo}</div>}
          </PageSubtitle>
        </div>
        <Button $variant="secondary" onClick={() => navigate('/interno/estoque/transferencias')}>‹ Transferências</Button>
      </div>

      {acao === 'DESPACHAR' && podeAgir && (
        <Bloco titulo="Despacho">
          <div style={{ fontSize: 13, color: BD.grayText, marginBottom: 8 }}>O material sai da origem e fica em trânsito. Ele não some do sistema: o trânsito é um local como outro qualquer no razão.</div>
          <Field><label>Nome da transportadora (opcional)</label><Input value={transportadora} maxLength={120} onChange={(e) => setTransportadora(e.target.value)} /></Field>
          <Field><label>Código de rastreio (opcional)</label><Input value={codigoRastreio} maxLength={80} onChange={(e) => setCodigoRastreio(e.target.value)} /></Field>
        </Bloco>
      )}

      {acao === 'RECEBER' && podeAgir && (
        <Bloco titulo="Recebimento">
          <div style={{ fontSize: 13, color: BD.grayText }}>Receber registra a chegada da caixa. O material ainda não conta no estoque do destino: quem faz isso é a conferência.</div>
        </Bloco>
      )}

      <Bloco titulo={`Itens · ${t.itens.length}`}>
        <TabelaWrap>
          <Table>
            <thead><tr><th>Item</th><th>Serial</th><th style={{ textAlign: 'right' }}>Enviado</th><th style={{ textAlign: 'right' }}>Recebido</th><th>Chegada</th></tr></thead>
            <tbody>
              {t.itens.map((l) => (
                <tr key={l.id}>
                  <td>
                    {l.itemNome}{l.itemCodigo ? <span style={{ fontSize: 11, color: BD.grayText }}> · {l.itemCodigo}</span> : null}
                    {l.livreNaOrigem != null && (
                      <div style={{ fontSize: 11, color: l.livreNaOrigem < (l.pedidoDoItem ?? l.quantidade) ? BD.red : BD.grayText }}>
                        livre na origem: {l.livreNaOrigem}
                        {l.pedidoDoItem != null && l.pedidoDoItem !== l.quantidade ? ` · pedido do item: ${l.pedidoDoItem}` : ''}
                      </div>
                    )}
                  </td>
                  <td style={{ fontFamily: 'monospace' }}>{l.serial ?? '—'}</td>
                  <td style={{ textAlign: 'right' }}>{l.quantidade}</td>
                  <td style={{ textAlign: 'right' }}>{t.status === 'CONFERIDA' ? l.quantidadeRecebida : '—'}</td>
                  <td style={{ fontSize: 13, color: chegadaDaLinha(l).parcial ? SEVERIDADE.atencao.texto : undefined }}>{chegadaDaLinha(l).texto}{l.observacao ? ` · ${l.observacao}` : ''}</td>
                </tr>
              ))}
              {t.itens.length === 0 && <tr><td colSpan={5} style={{ color: BD.grayText, padding: 12 }}>Esta transferência não tem itens: o despacho vai recusá-la.</td></tr>}
            </tbody>
          </Table>
        </TabelaWrap>

        <ListaCartoes>
          {t.itens.map((l) => (
            <Card key={l.id} style={{ padding: 12 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <b style={{ flexGrow: 1, fontSize: 15 }}>{l.itemNome}</b>
                <span style={{ fontFamily: 'monospace', fontSize: 15, fontWeight: 600 }}>{l.quantidade}</span>
              </div>
              <div style={{ marginTop: 2, fontSize: 12, color: BD.grayText }}>
                {l.serial ? <span style={{ fontFamily: 'monospace' }}>{l.serial}</span> : l.itemCodigo}
                {t.status === 'CONFERIDA' ? ` · recebido ${l.quantidadeRecebida}` : ''}
                {l.condicaoChegada ? ` · ${chegadaDaLinha(l).texto}` : ''}
              </div>
              {l.livreNaOrigem != null && (
                <div style={{ marginTop: 8 }}>
                  <StatusBadge
                    $bg={l.livreNaOrigem < (l.pedidoDoItem ?? l.quantidade) ? BD.redLight : '#D1FAE5'}
                    $color={l.livreNaOrigem < (l.pedidoDoItem ?? l.quantidade) ? BD.redDark : '#065F46'}
                  >
                    livre na origem: {l.livreNaOrigem}
                  </StatusBadge>
                  {l.pedidoDoItem != null && l.pedidoDoItem !== l.quantidade && (
                    <span style={{ marginLeft: 8, fontSize: 12, color: BD.grayText }}>pedido do item: {l.pedidoDoItem}</span>
                  )}
                </div>
              )}
            </Card>
          ))}
          {t.itens.length === 0 && <Card style={{ padding: 12, color: BD.grayText }}>Esta transferência não tem itens: o despacho vai recusá-la.</Card>}
        </ListaCartoes>
      </Bloco>

      {acao === 'CONFERIR' && (
        <Bloco titulo="Conferência item a item">
          {!podeAgir ? (
            <div style={{ fontSize: 13, color: BD.grayText }}>{doLado ?? 'Conferir exige a permissão "Transferir entre praças".'}</div>
          ) : (
            <>
              <div style={{ fontSize: 13, color: BD.grayText, marginBottom: 8 }}>
                Uma decisão por linha, e é aqui que o material entra no estoque do destino. Consumível pede a quantidade recebida (em branco não é zero) e o que faltar é lançado como extravio. Avariado e sucata só existem em peça: saldo não tem condição.
              </div>
              {t.itens.some((x) => x.controle === 'SERIAL') && <LeitorContinuo titulo={`Chegada ${t.numero}`} aoLer={lerChegada} disabled={busy} />}
              <TabelaWrap>
                <Table>
                  <thead><tr><th>Item</th><th>Serial</th><th style={{ textAlign: 'right' }}>Enviado</th><th>Decisão</th><th>Recebido</th><th>Observação</th></tr></thead>
                  <tbody>
                    {t.itens.map((l) => {
                      const x = conf[l.id] || { decisao: '', quantidadeRecebida: '', observacao: '' }
                      const falta = l.controle === 'QUANTIDADE' && x.decisao && x.decisao !== 'NAO_RETORNOU' && x.quantidadeRecebida.trim() !== ''
                        ? faltaDaConferencia(l, Number(x.quantidadeRecebida))
                        : 0
                      return (
                        <tr key={l.id}>
                          <td>{l.itemNome}</td>
                          <td style={{ fontFamily: 'monospace' }}>{l.serial ?? '—'}</td>
                          <td style={{ textAlign: 'right' }}>{l.quantidade}</td>
                          <td>
                            <Select value={x.decisao} onChange={(e) => setLinha(l, { decisao: e.target.value as DecisaoConferencia | '' })}>
                              <option value="">— decida —</option>
                              {decisoesDaLinha(l.controle).map((d) => (
                                <option key={d} value={d}>{rotuloDecisao('CHEGADA', d)}</option>
                              ))}
                            </Select>
                          </td>
                          <td>
                            {l.controle === 'QUANTIDADE' && x.decisao && x.decisao !== 'NAO_RETORNOU' ? (
                              <>
                                <Input type="number" min={0} max={l.quantidade} style={{ width: 90 }} value={x.quantidadeRecebida}
                                       onChange={(e) => setLinha(l, { quantidadeRecebida: e.target.value })} />
                                {falta > 0 && <div style={{ fontSize: 11, color: BD.red }}>{falta} vira extravio</div>}
                              </>
                            ) : (
                              <span style={{ color: BD.grayText }}>{x.decisao === 'NAO_RETORNOU' ? '0' : l.controle === 'SERIAL' && x.decisao ? '1' : ''}</span>
                            )}
                          </td>
                          <td><Input value={x.observacao} maxLength={400} placeholder="opcional" onChange={(e) => setLinha(l, { observacao: e.target.value })} /></td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              </TabelaWrap>

              <ListaCartoes>
                {t.itens.map((l) => {
                  const x = conf[l.id] || { decisao: '', quantidadeRecebida: '', observacao: '' }
                  return (
                    <Card key={l.id} style={{ padding: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                        <b style={{ flexGrow: 1, fontSize: 15 }}>{l.itemNome}</b>
                        <span style={{ fontFamily: 'monospace', fontSize: 12, color: BD.grayText }}>{l.serial ?? '—'}</span>
                      </div>
                      <div style={{ marginTop: 2, fontSize: 12, color: BD.grayText }}>enviado {l.quantidade}</div>
                      <div style={{ marginTop: 10 }}>
                        <Select value={x.decisao} onChange={(e) => setLinha(l, { decisao: e.target.value as DecisaoConferencia | '' })}>
                          <option value="">— decida —</option>
                          {decisoesDaLinha(l.controle).map((d) => (
                            <option key={d} value={d}>{rotuloDecisao('CHEGADA', d)}</option>
                          ))}
                        </Select>
                      </div>
                      {l.controle === 'QUANTIDADE' && x.decisao && x.decisao !== 'NAO_RETORNOU' && (
                        <div style={{ marginTop: 10 }}>
                          <Stepper
                            valor={x.quantidadeRecebida}
                            aoMudar={(v) => setLinha(l, { quantidadeRecebida: v })}
                            rotulo={`recebido de ${l.itemNome}`}
                            max={Number(l.quantidade)}
                          />
                          {x.quantidadeRecebida.trim() !== '' && faltaDaConferencia(l, Number(x.quantidadeRecebida)) > 0 && (
                            <div style={{ marginTop: 6, fontSize: 12, color: BD.redDark, fontWeight: 600 }}>
                              {faltaDaConferencia(l, Number(x.quantidadeRecebida))} vira extravio
                            </div>
                          )}
                        </div>
                      )}
                      <div style={{ marginTop: 10 }}>
                        <Input value={x.observacao} maxLength={400} placeholder="Observação (opcional)" onChange={(e) => setLinha(l, { observacao: e.target.value })} />
                      </div>
                    </Card>
                  )
                })}
              </ListaCartoes>

              {impConf && <div style={{ marginTop: 12, fontSize: 13, color: BD.grayText }}>{impConf}</div>}
            </>
          )}
        </Bloco>
      )}

      {historico.length > 0 && (
        <Bloco titulo="Histórico">
          <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {historico.map((h, i) => (
              <li key={`${h.statusPara}-${i}`} style={{ padding: '8px 0', borderTop: i ? `1px solid ${BD.grayBorder}` : 'none', fontSize: 13 }}>
                <div><b>{textoDoEvento(h)}</b> <span style={{ color: BD.grayText }}>· {fmtDateTime(h.criadoEm)}{h.tipo === 'NOTA' && h.usuarioNome ? ` · ${h.usuarioNome}` : ''}</span></div>
                {h.motivo && h.tipo !== 'NOTA' && <div style={{ marginTop: 2, color: BD.grayText }}>Motivo: {h.motivo}</div>}
              </li>
            ))}
          </ol>
        </Bloco>
      )}

      </div>

      <Decisao
        estado={ui}
        desde={`${desde.rotulo} ${fmtDateTime(desde.em)}`}
        aviso={null}
        principal={principal}
        secundarias={secundarias}
        pendencia={cancelavel ? 'Cancelar vale enquanto nada saiu da origem. Depois do despacho, o material está na estrada e precisa chegar.' : null}
        antesDasSaidas={campoMotivoCancelamento}
        busy={busy}
        aoFechar={desarmarCancelamento}
      />
    </DetailLayout>
  )
}

export default EstoqueTransferencia
